import { useEffect, useRef, useState, type ReactNode } from 'react'
import '@/vendor/sprout-loader.js'
import { TopicGlyph } from '@/components/gg/TopicGlyph'
import { SEED_COSTS } from '@/hooks/useSeeds'
import { FeedPhoto } from './FeedPhoto'
import type { FeedHeadline } from './feedTypes'
import { ageOf, asOf, hitOf, isRecord, isStale, labelOf, noDash, sourcesLabel } from './feedText'
import { WATCH_TYPES, type Alert } from '@/components/alerts/types'

/**
 * One story in Your feed (Brian's headlines.html v6.7 / v7.1): a tall post.
 * The topic pill, the headline (up to four lines, the keyword marked), the
 * picture at about 4:5 with the summary and Your angle on glass over its foot
 * (up to six lines each), then Build · 12, ＋ Newsletter and ×, then the outlet,
 * what kind of story it is and "Why this?".
 */

const X = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>

function marked(text: string, hit: string | null): ReactNode {
  if (!hit) return text
  const i = text.toLowerCase().indexOf(hit.toLowerCase())
  if (i < 0) return text
  return <>{text.slice(0, i)}<mark className="feed-kw">{text.slice(i, i + hit.length)}</mark>{text.slice(i + hit.length)}</>
}

export interface CardHandlers {
  onBuild: (h: FeedHeadline) => void
  onRemove: (h: FeedHeadline) => void
  onWhy: (h: FeedHeadline) => void
  onNewsletter: (h: FeedHeadline) => void
  onOpen: (h: FeedHeadline) => void
}

export function FeedCardBody({ h, inNewsletter, nlBusy, handlers }: {
  h: FeedHeadline
  inNewsletter: boolean
  nlBusy: boolean
  handlers?: CardHandlers
}) {
  const hit = hitOf(h)
  const rec = isRecord(h)
  const old = isStale(h)
  const dek = noDash(h.summary)
  const angle = noDash(h.angle)
  const stop = (fn?: (h: FeedHeadline) => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn?.(h) }
  return (
    <>
      <span className="feed-tpill"><TopicGlyph topic={h.topic} size={22} />{labelOf(h)}</span>
      <h2 className="feed-hl">{marked(noDash(h.headline), hit)}</h2>
      <div className="feed-pg">
        <FeedPhoto url={h.image_url} credit={h.image_credit} topic={h.topic} seedKey={h.id} story={{ id: h.id, topic: h.topic, headline: h.headline, summary: h.summary }} />
        {(dek || angle) && (
          <div className="feed-glass">
            {dek && <p className="feed-dek">{dek}</p>}
            {angle && <div className="feed-ang"><div className="feed-ang__k">Your angle</div><p>{angle}</p></div>}
          </div>
        )}
      </div>
      <div className="feed-acts">
        <button type="button" className="feed-bld" onClick={stop(handlers?.onBuild)}>Build<small>· {SEED_COSTS.build_story}</small></button>
        <button type="button" className={`feed-nlb${inNewsletter ? ' is-on' : ''}`} onClick={stop(handlers?.onNewsletter)} disabled={nlBusy}>
          {nlBusy ? 'Writing…' : inNewsletter ? '✓ In this week’s newsletter' : '＋ Newsletter'}
        </button>
        <button type="button" className="feed-x" onClick={stop(handlers?.onRemove)} aria-label="Remove this story">{X}</button>
      </div>
      <div className="feed-foot">
        <span className="feed-out">{h.source_name || 'Source'}</span>
        <span className="feed-fr">
          {rec && <span className="feed-seal" title="Found in a public record: an agenda, a permit or a license. You may be early on it.">Public record</span>}
          {hit
            ? <span className="feed-kwhit">{rec ? 'Hit' : 'Keyword hit'}</span>
            : <span className="feed-cls">{rec ? '' : `${sourcesLabel(h)} · `}<span className={old ? 'feed-old' : ''} title={old ? 'Older than a week: check it’s still current before you post' : undefined}>{ageOf(h)}{old ? ' old' : ''}</span></span>}
        </span>
        <button type="button" className="feed-whyb" onClick={stop(handlers?.onWhy)}>Why this?</button>
      </div>
    </>
  )
}

export function FeedCard(props: {
  h: FeedHeadline
  inNewsletter: boolean
  nlBusy: boolean
  going?: boolean
  handlers: CardHandlers
}) {
  return (
    <article className={`feed-card${props.going ? ' is-going' : ''}`} data-id={props.h.id} onClick={() => props.handlers.onOpen(props.h)}>
      <FeedCardBody {...props} />
    </article>
  )
}

// ---------------- A breaking alert (v2.5, in the v7 list) ----------------

/** The watch type's name: "Safety Alerts", "Snow & Weather". */
export const alertLabel = (a: Alert) => (WATCH_TYPES as Record<string, { name: string }>)[a.type]?.name ?? 'Breaking'

/**
 * An alert that broke today, at the top of Today with the amber border: the
 * type's pill, the headline, the picture, the dek, "Breaking as of …", then
 * Build · 12 and × (no ＋ Newsletter), the outlet and the Breaking news chip.
 * A tap opens the alert.
 */
export function BreakingCard({ a, going, onBuild, onRemove, onOpen }: {
  a: Alert
  going?: boolean
  onBuild: (a: Alert) => void
  onRemove: (a: Alert) => void
  onOpen: (a: Alert) => void
}) {
  const stop = (fn: (a: Alert) => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(a) }
  return (
    <article className={`feed-card feed-card--brk${going ? ' is-going' : ''}`} data-id={`b_${a.id}`} onClick={() => onOpen(a)}>
      <span className="feed-bpill"><TopicGlyph topic={a.type} size={18} />{alertLabel(a)}</span>
      <h2 className="feed-hl feed-hl--brk">{noDash(a.headline)}</h2>
      <FeedPhoto className="feed-brkpic" url={null} topic={a.type} seedKey={a.id} story={{ id: a.headline_id ?? `alert:${a.id}`, topic: a.type, headline: a.headline, summary: a.dek }} />
      {a.dek && <p className="feed-bdek">{noDash(a.dek)}</p>}
      <div className="feed-asof">{asOf(a.broke_at || a.delivered_at)}</div>
      <div className="feed-acts">
        <button type="button" className="feed-bld" onClick={stop(onBuild)}>Build<small>· {SEED_COSTS.build_story}</small></button>
        <button type="button" className="feed-x" onClick={stop(onRemove)} aria-label="Remove this story">{X}</button>
      </div>
      <div className="feed-foot">
        <span className="feed-out">{a.outlet}</span>
        <span className="feed-bchip">Breaking news</span>
      </div>
    </article>
  )
}

// ---------------- A story being built (v6.6) ----------------

const STEPS = ['Finding the story', 'Reading the sources', 'Checking it’s local to you', 'Writing the summary', 'Writing your angle']

interface SproutApi { svg?: () => string; paint?: (el: SVGSVGElement, v: number) => void }
const sproutApi = () => (window as unknown as { SproutLoader?: SproutApi }).SproutLoader

/**
 * All the new cards build at once. Each grows its sprout at its own pace
 * behind frosted glass (the card already its story's height once the story is
 * known); when the story is written it shows through the frost, the pane
 * clears, and the real card takes its place.
 *
 * The real pull takes half a minute or more and reports no steps, so each
 * card's sprout follows the elapsed time toward 95%, in spurts and pauses of
 * its own (k), and lands when the stories do, a beat apart.
 */
export function BuildingCard({ slot, k, started, h, landedAt, failed, onDone, children }: {
  slot: number
  k: number
  started: number
  /** The story this slot became, once the pull is back. */
  h: FeedHeadline | null
  /** When the pull came back (null while it is still out). */
  landedAt: number | null
  failed?: boolean
  onDone: () => void
  children?: ReactNode
}) {
  const [now, setNow] = useState(() => Date.now())
  const [phase, setPhase] = useState<'grow' | 'ready' | 'show' | 'clear'>('grow')
  const el = useRef<HTMLElement>(null)
  const sprout = useRef<HTMLDivElement>(null)
  const peak = useRef(0)
  const doneRef = useRef(onDone)
  doneRef.current = onDone

  // Grow: from 0 toward .95 over this card's own expected time, never backward.
  const expect = 22000 + slot * 2600 + (k % 3000)
  const t = Math.max(0, now - started) / expect
  const a = 0.35 + (k % 5) * 0.1, m = 1 + (k % 3)
  const raw = 0.95 * (1 - Math.exp(-2.2 * (t - (a * Math.sin(2 * Math.PI * m * Math.min(t, 1))) / (2 * Math.PI * m))))
  peak.current = Math.max(peak.current, Math.min(0.95, Math.max(0, raw)))
  const p = phase === 'grow' ? peak.current : 1

  useEffect(() => {
    if (phase !== 'grow') return
    const iv = setInterval(() => setNow(Date.now()), 120)
    return () => clearInterval(iv)
  }, [phase])

  useEffect(() => {
    const api = sproutApi()
    if (sprout.current && api?.svg && !sprout.current.firstChild) sprout.current.innerHTML = api.svg()
    const svg = sprout.current?.querySelector('svg')
    if (svg && api?.paint) api.paint(svg as SVGSVGElement, p * 100)
  }, [p])

  // Landed: Ready, the story through the frost, the frost clears, the real card.
  useEffect(() => {
    if (failed) { doneRef.current(); return }
    if (landedAt == null) return
    if (!h) { doneRef.current(); return }
    const at = Math.max(0, landedAt + slot * 450 + (k % 400) - Date.now())
    const timers = [
      setTimeout(() => setPhase('ready'), at),
      setTimeout(() => {
        if (el.current) el.current.style.height = `${el.current.scrollHeight}px`
        setPhase('show')
      }, at + 200),
      setTimeout(() => setPhase('clear'), at + 1000),
      setTimeout(() => doneRef.current(), at + 1850),
    ]
    return () => timers.forEach(clearTimeout)
  }, [landedAt, h, failed, slot, k])

  const step = phase !== 'grow' ? 'Ready' : now < started + 400 ? 'Starting' : STEPS[Math.min(STEPS.length - 1, Math.floor(p * STEPS.length))]
  return (
    <article ref={el} className={`feed-card feed-skel${phase === 'show' || phase === 'clear' ? ' is-show' : ''}${phase === 'clear' ? ' is-clear' : ''}`} aria-busy="true">
      {h && <div className="feed-skel__story" inert aria-hidden="true">{children}</div>}
      <div className="feed-skel__glass">
        <div className="feed-skel__sprout" ref={sprout} />
        <div className="feed-skel__k"><span>{step}</span><span className="feed-skel__pc">{Math.round(p * 100)}%</span></div>
      </div>
    </article>
  )
}
